import { Fragment, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { Link as ScrollLink } from 'react-scroll';
import emailjs from '@emailjs/browser';
import { FaHtml5, FaCss3Alt, FaJs, FaReact, FaGithub, FaPython, FaLinkedin, FaCss3, FaGitAlt, FaAngular, FaExternalLinkAlt } from 'react-icons/fa';
import { SiDotnet, SiTypescript, SiMongodb, SiAuth0, SiPostman } from 'react-icons/si';
import './styles/global.css';

import GCSEC from './assets/images/GCSEC.jpg';
import cfbApp from './assets/images/cfbApp.jpg';

const sections = ["about", "experience", "skills", "projects", "contact"];

const skillGroups = [
  {
    title: 'Front End',
    items: [
      { icon: FaHtml5, name: 'HTML' },
      { icon: FaCss3Alt, name: 'CSS' },
      { icon: FaJs, name: 'JavaScript' },
      { icon: SiTypescript, name: 'TypeScript' },
      { icon: FaAngular, name: 'Angular' },
      { icon: FaReact, name: 'React' },
    ],
  },
  {
    title: 'Back End & Data',
    items: [
      { icon: SiDotnet, name: 'C# / .NET' },
      { icon: SiMongodb, name: 'MongoDB' },
      { icon: SiAuth0, name: 'Auth0' },
      { icon: FaPython, name: 'Python' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { icon: FaGitAlt, name: 'Git' },
      { icon: SiPostman, name: 'Postman' },
    ],
  },
];

const experience = [
  {
    role: 'Software Engineer & Technical Support',
    dates: 'June 2025 – Present',
    company: 'Wavelogix · West Lafayette, IN',
    points: [
      'Build and maintain the customer-facing dashboard in Angular.',
      'Design and develop REST APIs in C#, backed by MongoDB.',
      'Handle authentication and authorization with Auth0.',
      'Support customers on the platform directly, which keeps me close to the product.',
    ],
  },
  {
    role: 'Student Web Developer',
    company: 'Purdue Polytechnic Institute, Marketing Department · West Lafayette, IN',
    dates: 'June 2024 – June 2025',
    points: [
      'Created, maintained, and migrated 30+ Drupal-based webpages using HTML, CSS, and templating tools, keeping the site on-brand and accessible.',
      'Developed responsive components and site features that improved navigation and mobile usability for prospective students and faculty.',
      'Gathered requirements from marketing staff to plan and ship updates, balancing design goals with technical constraints.',
    ],
  },
];

const projects = [
  {
    id: 1,
    title: 'CFB Stats Dashboard',
    image: cfbApp,
    description: 'A college football stats dashboard powered by the CollegeFootballData API. It features a weekly scoreboard filterable by season, week, conference, and AP Top 25, with game results, betting spreads and over/unders, and TV networks, plus dedicated views for teams, team comparisons, and players.',
    tech: [
      { icon: FaReact, name: '' },
      { icon: SiTypescript, name: '' },
      { icon: FaCss3, name: '' },
    ],
    link: 'https://cfb-app-roan.vercel.app/',
  },
  {
    id: 2,
    title: 'GCSEC Website',
    image: GCSEC,
    description: 'A full-featured React web application developed in collaboration with two other developers and a team of several UX designers for a special education school. The site features dynamic routing using React Router, allowing users to explore department pages, staff profiles, and community resources.',
    tech: [
      { icon: FaReact, name: '' },
      { icon: FaCss3, name: '' },
      { icon: FaJs, name: '' },
      { icon: FaGitAlt, name: '' },
    ],
    link: 'https://gcsec-staging.netlify.app',
  },
];


function ExperienceConnector() {
  return (
    <svg viewBox="0 0 200 90" className="w-40 h-20 mx-auto my-1 overflow-visible" fill="none" aria-hidden="true">
      <motion.path
        d="M100 4 C 170 24, 30 66, 100 86"
        stroke="#588157"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="1 8"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 1, ease: 'easeInOut' }}
      />
      <motion.circle cx="100" cy="4" r="5" fill="#588157" initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} />
      <motion.circle cx="100" cy="86" r="5" fill="#588157" initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.9 }} />
    </svg>
  );
}

function SendingDots() {
  return (
    <span className="inline-flex gap-1 items-center justify-center h-5" aria-label="Sending">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-2 h-2 rounded-full bg-white"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.12 }}
        />
      ))}
    </span>
  );
}

function SuccessPanel({ onReset }) {
  const sparks = Array.from({ length: 14 }, (_, i) => {
    const angle = (i / 14) * Math.PI * 2;
    return { x: Math.cos(angle) * 70, y: Math.sin(angle) * 70, size: i % 2 ? 6 : 9 };
  });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-md mx-auto flex flex-col items-center text-center py-8"
      role="status"
    >
      <div className="relative w-28 h-28 flex items-center justify-center">
        {sparks.map((spark, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-[#588157]"
            style={{ width: spark.size, height: spark.size }}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
            animate={{ x: spark.x, y: spark.y, opacity: [0, 1, 0], scale: [0, 1, 0.4] }}
            transition={{ duration: 0.9, delay: 0.55, ease: 'easeOut' }}
          />
        ))}
        <svg viewBox="0 0 100 100" className="w-28 h-28" fill="none" stroke="#588157" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
          <motion.circle
            cx="50" cy="50" r="42"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
          />
          <motion.path
            d="M30 52 L44 66 L71 36"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.5, ease: 'easeOut' }}
          />
        </svg>
      </div>
      <motion.h3
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="text-2xl font-bold text-[#588157] mt-4"
      >
        Message sent
      </motion.h3>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.95 }}
        className="mt-2 text-sm sm:text-base"
      >
        Thanks for reaching out! I'll get back to you soon.
      </motion.p>
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
        onClick={onReset}
        className="mt-6 text-sm text-[#588157] underline"
      >
        Send another message
      </motion.button>
    </motion.div>
  );
}

export default function App() {
  const formRef = useRef();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null);
  const [activeSection, setActiveSection] = useState("hero");
  const [loading, setLoading] = useState(false);

  const handleScroll = () => {
    const sectionOffsets = sections.map((id) => {
      const el = document.getElementById(id);
      return { id, offset: el?.offsetTop || 0 };
    });
    const scrollY = window.scrollY + 100;
    for (let i = sectionOffsets.length - 1; i >= 0; i--) {
      if (scrollY >= sectionOffsets[i].offset) {
        setActiveSection(sectionOffsets[i].id);
        break;
      }
    }
  };

  useEffect(() => {
    if (status === 'error') {
      const timer = setTimeout(() => setStatus(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    emailjs.sendForm('service_r51cczj', 'template_ssg3m8v', formRef.current, 'T31UkbXOlDq0-cQaO')
    .then(() => {
      formRef.current.reset();
      setFormData({ name: '', email: '', message: '' });
      setStatus('success');
    })
    .catch(() => setStatus('error'))
    .finally(() => setLoading(false));
  };

  return (
    <div className="font-[Inter] text-gray-900 bg-white">
      <nav className="md:hidden fixed top-0 left-0 right-0 z-10 bg-white/90 backdrop-blur shadow-sm px-4 py-3 flex justify-between items-center text-xs">
        <ScrollLink to="hero" smooth={true} duration={500} className="cursor-pointer text-[#588157] font-bold">AM</ScrollLink>
        <div className="flex gap-3">
          {sections.map((id) => (
            <ScrollLink
              key={id}
              to={id}
              smooth={true}
              duration={500}
              className={`cursor-pointer ${activeSection === id ? 'text-[#588157] font-semibold' : ''}`}
            >
              {id.charAt(0).toUpperCase() + id.slice(1)}
            </ScrollLink>
          ))}
        </div>
      </nav>

      <div className="hidden md:block fixed top-0 left-0 right-0 z-10 bg-transparent">
        <nav className="flex justify-between items-center px-6 py-3 sticky top-0 glassmorphism rounded-full w-[92%] lg:w-[75%] xl:w-[60%] max-w-5xl mx-auto mt-4 shadow-md gap-4">
          
          {/* Left: Name */}
          <ScrollLink
            to="hero"
            smooth={true}
            duration={500}
            className="cursor-pointer text-[#588157] font-bold text-base shrink-0"
          >
            AM
          </ScrollLink>

          {/* Center: Navigation Links */}
          <div className="flex gap-4 lg:gap-6 text-sm lg:text-base">
            {sections.map((id) => (
              <ScrollLink
                key={id}
                to={id}
                smooth={true}
                duration={500}
                className={`cursor-pointer ${activeSection === id ? 'text-[#588157]' : ''}`}
              >
                {id.charAt(0).toUpperCase() + id.slice(1)}
              </ScrollLink>
            ))}
          </div>

          {/* Right: Social Icons */}
          <div className="flex gap-3 lg:gap-4 text-[#588157] shrink-0">
            <a href="https://www.linkedin.com/in/-adrian-martinez-/" target="_blank" rel="noopener noreferrer">
              <FaLinkedin size={20} />
            </a>
            <a href="https://github.com/amart48" target="_blank" rel="noopener noreferrer">
              <FaGithub size={20} />
            </a>
          </div>
        </nav>
      </div>

      <section id="hero" className="min-h-screen grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto px-6 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center lg:text-left"
        >
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold">Adrian Martinez</h1>
          <h2 className="text-xl sm:text-2xl md:text-4xl text-[#588157] mt-4">Software Engineer</h2>
          <p className="mt-6 max-w-md mx-auto lg:mx-0 text-sm sm:text-base">
            I am a software engineer based in West Lafayette, Indiana. I build web applications end to end, from Angular and React interfaces to C# REST APIs.
          </p>

          <ScrollLink
            to="projects"
            smooth={true}
            duration={500}
            className="bg-[#588157] mt-6 inline-block text-white py-2 px-5 sm:px-6 rounded-full cursor-pointer hover:bg-[#87c38f] transition-colors text-sm sm:text-base"
          >
            My Projects
          </ScrollLink>
          <ScrollLink
            to="contact"
            smooth={true}
            duration={500}
            className="mt-6 ml-3 inline-block border border-[#588157] text-[#588157] py-2 px-5 sm:px-6 rounded-full cursor-pointer hover:bg-[#588157] hover:text-white transition-colors text-sm sm:text-base"
          >
            Get in Touch
          </ScrollLink>
        </motion.div>

        <motion.div
          id="about"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:border-l-4 lg:border-[#588157] lg:pl-10"
        >
          <p className="text-3xl sm:text-4xl font-bold text-[#588157] mb-6 text-center lg:text-left">
            About Me
          </p>
          <div className="space-y-4 text-sm sm:text-base text-center lg:text-left">
            <p>
              I’m a software engineer who builds accessible web apps. I graduated from Purdue with a degree in Computer Graphics Technology and now work full-time at a West Lafayette startup, where I split my time between working on our customer-facing dashboard and the backend for that dashboard and supporting the customers who use it.
            </p>
            <p>
              On the front end I use tools such as Angular, React, and Next.js, and C# on the back end, where I build REST APIs with MongoDB. Outside of work, I enjoy listening to music, playing music, watching sports, and staying active.
            </p>
          </div>
        </motion.div>
      </section>

      <section id="experience" className="py-16 px-4 sm:px-6 bg-gray-100">
        <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#588157] mb-8 sm:mb-10 text-center">
          Experience
        </p>
        <div className="max-w-2xl mx-auto flex flex-col">
          {experience.map((job, index) => (
            <Fragment key={job.role}>
            {index > 0 && <ExperienceConnector />}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(88, 129, 87, 0.28)' }}
              transition={{ duration: 0.3 }}
              className="bg-white border border-transparent hover:border-[#588157]/40 rounded-xl shadow-lg p-6 cursor-default"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1">
                <h3 className="text-xl font-bold">{job.role}</h3>
                {job.dates && <span className="text-sm text-[#588157] font-semibold">{job.dates}</span>}
              </div>
              <p className="text-sm text-gray-500 mb-4">{job.company}</p>
              <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base">
                {job.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </motion.div>
            </Fragment>
          ))}
        </div>
      </section>

      <section id="skills" className="py-16 px-4 sm:px-6 flex flex-col items-center">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#588157] mb-8 sm:mb-10 text-center">
          My Skills
        </h2>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col gap-10 max-w-3xl">
            {skillGroups.map((group) => (
              <div key={group.title} className="text-center">
                <p className="text-sm uppercase tracking-widest text-gray-500 mb-4">{group.title}</p>
                <div className="flex justify-center gap-8 sm:gap-10 flex-wrap">
                  {group.items.map(({ icon: Icon, name }) => (
                    <div
                      key={name}
                      className="flex flex-col items-center gap-2 text-[#588157] transform transition-transform duration-300 hover:scale-110"
                    >
                      <Icon size={48} />
                      <span className="text-xs sm:text-sm text-gray-700">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>


      <section id="projects" className="py-16 px-4 sm:px-6 md:px-20 bg-gray-100">
        <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#588157] mb-6 sm:mb-10 text-center">
          Projects
        </p>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {projects.map((project) => (
              <div key={project.id} className="group flex flex-col bg-white border border-transparent rounded-xl p-4 shadow-xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-[#588157]/40">
                <a href={project.link} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded">
                  <img src={project.image} alt={project.title} className="rounded w-full h-auto transition-transform duration-500 group-hover:scale-105" />
                </a>
                <h3 className="mt-4 text-xl font-bold text-[#588157]">{project.title}</h3>
                <p className="my-4 text-sm sm:text-base">{project.description}</p>
                <a href={project.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mb-4 self-start text-[#588157] underline text-sm sm:text-base">
                  View Live <FaExternalLinkAlt size={12} />
                </a>
                <div className="mt-auto flex gap-2 sm:gap-4 flex-wrap">
                  {project.tech.map((tech, i) => (
                    <span key={i} className="flex items-center gap-1 text-[#588157] text-sm sm:text-base">
                      {tech.icon ? <tech.icon className="text-lg sm:text-xl" /> : null}
                      {tech.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>


      <section id="contact" className="h-full py-16 px-4 sm:px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#588157] mb-6 sm:mb-10 text-center">
          Contact Me
        </h2>
        <h2 className="text-sm sm:text-base mb-8 sm:mb-10 text-center">
          Contact me directly at <a className="text-[#588157] underline" href="mailto:adrian--martinez@outlook.com">adrian--martinez@outlook.com</a> or using this form.
        </h2>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <SuccessPanel key="success" onReset={() => setStatus(null)} />
            ) : (
              <motion.div key="form" exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.25 }}>
                <form ref={formRef} onSubmit={handleSubmit} className="w-full max-w-md mx-auto flex flex-col gap-4">
            <input
              name="name"
              placeholder="Name"
              required
              onChange={handleChange}
              className="p-2 border rounded text-sm sm:text-base"
            />
            <input
              name="email"
              type="email"
              placeholder="Email"
              required
              onChange={handleChange}
              className="p-2 border rounded text-sm sm:text-base"
            />
            <textarea
              name="message"
              placeholder="Message"
              rows="5"
              required
              onChange={handleChange}
              className="p-2 border rounded text-sm sm:text-base"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-[#588157] text-white py-2 rounded text-sm sm:text-base hover:bg-[#87c38f] transition-colors disabled:opacity-60"
            >
              {loading ? <SendingDots /> : 'Send'}
            </button>
<AnimatePresence>
              {status === 'error' && (
                <motion.p
                  initial={{ opacity: 0, x: 0 }}
                  animate={{ opacity: 1, x: [0, -10, 10, -8, 8, 0] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center text-sm sm:text-base text-red-600"
                >
                  Something went wrong. Please try again or email me directly.
                </motion.p>
              )}
            </AnimatePresence>
          </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      <footer className="py-6 flex justify-center gap-6 align-middle text-base">
        © Adrian Martinez {new Date().getFullYear()}
        <a href="https://www.linkedin.com/in/-adrian-martinez-/" target="_blank" rel="noopener noreferrer" className="text-[#588157]"><FaLinkedin size={24}/></a>
        <a href="https://github.com/amart48" target="_blank" rel="noopener noreferrer" className="text-[#588157]"><FaGithub size={24}/></a>
      </footer>
    </div>
  );
}
